import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { rupee } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck, Loader2 } from "lucide-react";

const empty = { name: "", charge: "", is_active: true };

export default function AdminDelivery() {
  usePageMeta("Manage Delivery");
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/delivery-locations").then(({ data }) => setLocations(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setForm({ name: l.name, charge: l.charge, is_active: l.is_active }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Location name is required"); return; }
    setSaving(true);
    const payload = { name: form.name, charge: parseFloat(form.charge || 0), is_active: form.is_active };
    try {
      if (editing) await api.put(`/admin/delivery-locations/${editing.id}`, payload);
      else await api.post("/admin/delivery-locations", payload);
      toast.success(editing ? "Location updated" : "Location added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.name}"?`)) return;
    try { await api.delete(`/admin/delivery-locations/${l.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Delivery Locations</h1>
          <p className="mt-1 text-ink-soft">Set delivery areas and their charges. Global thresholds live in Settings.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="add-location-btn"><Plus className="mr-1 h-4 w-4" /> Add Location</Button>
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : locations.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-white p-12 text-center">
          <Truck className="mx-auto h-10 w-10 text-ink-muted" />
          <p className="mt-4 font-heading text-lg text-ink">No delivery areas yet</p>
          <p className="text-sm text-ink-muted">Add areas so customers can pick them at checkout.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {locations.map((l) => (
            <div key={l.id} className="rounded-2xl border border-border/60 bg-white p-5 shadow-ambient" data-testid={`admin-location-${l.id}`}>
              <div className="flex items-start justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand"><Truck className="h-5 w-5" /></span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${l.is_active ? "bg-sage/15 text-sage" : "bg-ink/10 text-ink-muted"}`}>{l.is_active ? "Active" : "Off"}</span>
              </div>
              <h3 className="mt-3 font-heading text-lg text-ink">{l.name}</h3>
              <p className="text-sm text-ink-soft">{l.charge > 0 ? `Delivery: ${rupee(l.charge)}` : "Free delivery"}</p>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(l)} className="rounded-full"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => remove(l)} className="rounded-full text-destructive"><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Location" : "Add Location"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Area / location name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-xl" placeholder="e.g. Gachibowli" data-testid="location-name-input" />
            </div>
            <div>
              <Label>Delivery charge (₹)</Label>
              <Input type="number" value={form.charge} onChange={(e) => setForm({ ...form, charge: e.target.value })} className="mt-1.5 rounded-xl" placeholder="0 for free" data-testid="location-charge-input" />
            </div>
            <div className="flex items-center justify-between rounded-xl bg-cream-alt/50 px-4 py-3">
              <Label>Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-brand text-ink" data-testid="save-location-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
